import React, { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import algoliasearch from 'algoliasearch/lite';
import { Route, Switch } from 'react-router-dom';

import { Loader } from '../../components/Loader';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { CustomPageLayout } from '../../containers/CustomPageLayout';
import { GET_HAS_NEWS } from '../../graphql/local';
import { GET_ALGOLIA_API_KEY } from '../../graphql/user';
import { SERP_ROUTES, INSIGHTS_SERP_ROUTE } from '../../routes';
import { LearnerInfo, Tenant } from '../../types';
import { DEFAULT_URL } from './const';
import { CategoryPage } from './pages/CategoryPage';
import { ExpertPage } from './pages/ExpertPage';
import { ExpertsPage } from './pages/ExpertsPage';
import { LandingPage } from './pages/LandingPage';
import { SearchPage } from './pages/SearchPage';
import { TermPage } from './pages/TermPage';

interface DiscoverPageProps {
  tenant: Tenant;
  userInfo?: LearnerInfo;
  userLoading: boolean;
}

export type GetHasNewsResponse = {
  hasNews?: boolean;
};

const algoliaName = process.env.REACT_APP_ALGOLIA_APP_NAME || '';

const DiscoverPage: React.FC<DiscoverPageProps> = ({ tenant, userInfo, userLoading }) => {
  const algoliaIndexName = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}_${tenant?.name}`;

  const { data, loading } = useQuery(GET_ALGOLIA_API_KEY, {
    skip: !userInfo,
  });

  const { data: dataNews } = useQuery<GetHasNewsResponse>(GET_HAS_NEWS);

  const routes = useMemo(
    () => (dataNews?.hasNews ? [...SERP_ROUTES, INSIGHTS_SERP_ROUTE] : SERP_ROUTES),
    [dataNews],
  );

  const algoliaApiKey =
    userInfo && data?.getAlgoliaApiKey?.api_key
      ? data?.getAlgoliaApiKey?.api_key
      : process.env.REACT_APP_ALGOLIA_SEARCH_KEY;

  const searchClient = useMemo(() => algoliasearch(algoliaName, algoliaApiKey), [algoliaApiKey]);
  const loadingKey = useMemo(() => userLoading || loading, [userLoading, loading]);
  const logoUrl = useMemo(
    () => (userInfo?.company?.logo_url ? userInfo?.company?.logo_url : tenant?.logo_url),
    [userInfo, tenant],
  );

  return (
    <>
      <Box>
        <Switch>
          <Route
            path={`${DEFAULT_URL}/glossary/:term`}
            render={(props) => (
              <TermPage
                userInfo={userInfo}
                tenant={tenant?.title}
                algoliaIndexName={algoliaIndexName}
                searchClient={searchClient}
                tenantName={tenant?.name}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            path={routes}
            render={(props) => (
              <SearchPage
                algoliaIndexName={algoliaIndexName}
                searchClient={searchClient}
                tenant={tenant}
                userInfo={userInfo}
                loadingKey={loadingKey}
                logoUrl={logoUrl}
                tenantName={tenant?.name}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            path={[
              '/expert/:expertSlug',
              '/experts/:expertSlug',
              `${DEFAULT_URL}/experts/:expertSlug`,
            ]}
            render={(props) => (
              <ExpertPage
                algoliaIndexName={algoliaIndexName}
                searchClient={searchClient}
                tenant={tenant}
                userInfo={userInfo}
                loadingKey={loadingKey}
                logoUrl={logoUrl}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            path={['/experts', `${DEFAULT_URL}/experts`]}
            render={(props) => (
              <ExpertsPage
                searchClient={searchClient}
                tenant={tenant}
                loadingKey={loadingKey}
                logoUrl={logoUrl}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            path={[`${DEFAULT_URL}/:category/:subcategory`, `${DEFAULT_URL}/:category`]}
            render={(props) => (
              <CategoryPage
                algoliaIndexName={algoliaIndexName}
                searchClient={searchClient}
                tenant={tenant}
                userInfo={userInfo}
                loadingKey={loadingKey}
                logoUrl={logoUrl}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          {!!userInfo && (
            <ProtectedRoute
              exact={false}
              userLoading={userLoading}
              userInfo={userInfo}
              path={`${DEFAULT_URL}`}
              render={(props) => (
                <LandingPage tenant={tenant} fallback={<Loader />} userInfo={userInfo} {...props} />
              )}
            />
          )}
          <Route
            path={DEFAULT_URL}
            render={(props) => <CustomPageLayout {...props} userInfo={userInfo} tenant={tenant} />}
          />
        </Switch>
      </Box>
    </>
  );
};

export default DiscoverPage;
